export default function PersonalTutoring() {
  const benefits = [
    {
      icon: 'ri-user-star-line',
      title: 'One-on-One Attention',
      description: 'Personalized instruction tailored to your specific needs and learning style'
    },
    {
      icon: 'ri-time-line',
      title: 'Flexible Scheduling',
      description: 'Choose session times that work best for your schedule'
    },
    {
      icon: 'ri-focus-3-line',
      title: 'Targeted Problem Solving',
      description: 'Focus on your specific challenges and weak areas'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Accelerated Progress',
      description: 'Achieve results faster with direct expert guidance'
    }
  ];

  const howItWorksSteps = [
    {
      number: '1',
      title: 'Fill the Form',
      description: 'Complete the booking form with your details and preferred schedule',
      icon: 'ri-file-list-3-line'
    },
    {
      number: '2',
      title: 'We Confirm Within 24hrs',
      description: 'Our team reviews your request and confirms your session time',
      icon: 'ri-checkbox-circle-line'
    },
    {
      number: '3',
      title: 'Your Session Begins',
      description: 'Meet with Mr. Augustine and start your personalized learning journey',
      icon: 'ri-graduation-cap-line'
    }
  ];

  return (
    <section className="py-24 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            <div className="inline-flex items-center gap-3 bg-amber-50 px-6 py-3 rounded-full mb-8">
              <i className="ri-vip-crown-line text-2xl text-amber-600"></i>
              <span className="text-lg font-semibold text-amber-700">Premium Service</span>
            </div>

            <h2 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">
              Personal Tutoring with the<br />
              <span className="text-emerald-600">Best Math Teacher</span>
            </h2>

            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              Get direct, personalized instruction from Augustine Anyimadu-Ahenkae. With 30+ years of experience and a Chancellor's Honor Award, receive the highest quality mathematics education available.
            </p>

            {/* Benefits Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-emerald-100 text-emerald-600 flex-shrink-0">
                    <i className={`${benefit.icon} text-2xl`}></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Pricing */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 mb-8">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Starting at</p>
                  <p className="text-4xl font-bold text-gray-900">$55<span className="text-xl text-gray-600">/hour</span></p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600 mb-1">Package deals available</p>
                  <p className="text-lg font-semibold text-emerald-600">Save up to 20%</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <i className="ri-checkbox-circle-line text-emerald-600"></i>
                <span>First consultation session free for Complete Access members</span>
              </div>
            </div>

            {/* CTA Button */}
            <button 
              onClick={() => {
                const element = document.getElementById('tutoring-form');
                element?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="w-full md:w-auto px-10 py-5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-lg font-semibold rounded-lg hover:opacity-90 transition-all duration-300 shadow-xl whitespace-nowrap cursor-pointer"
            >
              Book a Session
            </button>
          </div>

          {/* Right Image */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <div className="w-full h-[600px]">
                <img
                  src="https://static.readdy.ai/image/14fde9ba81a9738a8aa28fb63948ba18/8a7b4b0aec6e8023ac551ecc198440c6.jpeg"
                  alt="Personal Tutoring Session"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
            </div>

            {/* Floating Stats */}
            <div className="absolute -bottom-8 -left-8 bg-white rounded-2xl shadow-2xl p-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-emerald-100">
                  <i className="ri-user-follow-line text-3xl text-emerald-600"></i>
                </div>
                <div>
                  <p className="text-3xl font-bold text-gray-900">500+</p>
                  <p className="text-sm text-gray-600">Students Tutored</p>
                </div>
              </div>
            </div>

            <div className="absolute -top-8 -right-8 bg-white rounded-2xl shadow-2xl p-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-amber-100">
                  <i className="ri-star-line text-3xl text-amber-600"></i>
                </div>
                <div>
                  <p className="text-3xl font-bold text-gray-900">4.9/5</p>
                  <p className="text-sm text-gray-600">Tutoring Rating</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* How It Works Section */}
        <div className="mt-32">
          <div className="text-center mb-16">
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              How It Works
            </h3>
            <p className="text-lg text-gray-600">
              Simple steps to start your personalized tutoring journey
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {howItWorksSteps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 h-full border-2 border-emerald-100">
                  {/* Step Number Badge */}
                  <div className="absolute -top-6 left-8">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-xl font-bold shadow-lg">
                      {step.number}
                    </div>
                  </div>

                  {/* Icon */}
                  <div className="w-16 h-16 flex items-center justify-center rounded-xl bg-white mb-6 mt-4">
                    <i className={`${step.icon} text-3xl text-emerald-600`}></i>
                  </div>

                  {/* Content */}
                  <h4 className="text-2xl font-bold text-gray-900 mb-3">{step.title}</h4>
                  <p className="text-base text-gray-600 leading-relaxed">{step.description}</p>
                </div>

                {/* Arrow between steps */}
                {index < howItWorksSteps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <i className="ri-arrow-right-line text-3xl text-emerald-400"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}